/**
 * Philippine time, money and mobile numbers. Spec 001, contracts/ph-format.md.
 *
 * WHY ONE ZONE
 *
 * The Philippines has one time zone and no daylight saving. ServicePilot formatted times in the
 * DEVICE's zone, and on 26 September 2026 phones on another zone moved 28 bookings an hour early.
 * Here every format and every wall-clock input is Asia/Manila, so a computer in Sydney and a phone
 * in Cebu show the same 9:00 AM.
 *
 * This file has no imports, so `supabase/functions/_shared/ph.ts` can be an exact copy. The gate
 * fails when the two differ.
 */

export const TIME_ZONE = 'Asia/Manila'
export const LOCALE = 'en-PH'
export const CURRENCY = 'PHP'

type DateLike = Date | string | number

const toDate = (d: DateLike): Date => (d instanceof Date ? d : new Date(d))
const valid = (d: Date) => !Number.isNaN(d.getTime())

// Intl.DateTimeFormat is slow to construct; these are built once.
const F = {
  short: new Intl.DateTimeFormat(LOCALE, { timeZone: TIME_ZONE, day: 'numeric', month: 'short' }),
  medium: new Intl.DateTimeFormat(LOCALE, { timeZone: TIME_ZONE, day: 'numeric', month: 'short', year: 'numeric' }),
  long: new Intl.DateTimeFormat(LOCALE, { timeZone: TIME_ZONE, day: 'numeric', month: 'long', year: 'numeric' }),
  full: new Intl.DateTimeFormat(LOCALE, { timeZone: TIME_ZONE, weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }),
  weekday: new Intl.DateTimeFormat(LOCALE, { timeZone: TIME_ZONE, weekday: 'short', day: 'numeric', month: 'short' }),
  time: new Intl.DateTimeFormat(LOCALE, { timeZone: TIME_ZONE, hour: 'numeric', minute: '2-digit', hour12: true }),
  parts: new Intl.DateTimeFormat('en-US', {
    timeZone: TIME_ZONE, hour12: false, weekday: 'short',
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
  }),
}

// en-PH puts the month first ("Oct 14, 2026"), which is how Philippine documents write dates.

/** 'Oct 14' | 'Oct 14, 2026' | 'October 14, 2026' | 'Wednesday, October 14, 2026'. Empty for a bad date. */
export function fmtDate(d: DateLike | null | undefined, style: 'short' | 'medium' | 'long' | 'full' = 'medium'): string {
  if (d === null || d === undefined || d === '') return ''
  const x = toDate(d)
  return valid(x) ? F[style].format(x) : ''
}

/** 'Wed, Oct 14'. */
export function fmtWeekday(d: DateLike | null | undefined): string {
  if (d === null || d === undefined || d === '') return ''
  const x = toDate(d)
  return valid(x) ? F.weekday.format(x) : ''
}

/** '9:00 AM'. `h24` gives '09:00'. */
export function fmtTime(d: DateLike | null | undefined, opts?: { h24?: boolean }): string {
  if (d === null || d === undefined || d === '') return ''
  const x = toDate(d)
  if (!valid(x)) return ''
  if (opts?.h24) {
    const p = manilaParts(x)
    return `${String(p.hour).padStart(2, '0')}:${String(p.minute).padStart(2, '0')}`
  }
  return F.time.format(x)
}

/** 'Oct 14, 9:00 AM'. `year` gives 'Oct 14, 2026, 9:00 AM'. */
export function fmtDateTime(d: DateLike | null | undefined, opts?: { year?: boolean }): string {
  if (d === null || d === undefined || d === '') return ''
  const x = toDate(d)
  if (!valid(x)) return ''
  return `${(opts?.year ? F.medium : F.short).format(x)}, ${F.time.format(x)}`
}

const DATE_FIELDS = ['weekday', 'era', 'year', 'month', 'day', 'dateStyle'] as const
const TIME_FIELDS = ['hour', 'minute', 'second', 'dayPeriod', 'timeStyle', 'fractionalSecondDigits'] as const
const formatters = new Map<string, Intl.DateTimeFormat>()

/**
 * The drop-in for `date.toLocaleDateString(locale, opts)`, `toLocaleTimeString` and
 * `toLocaleString`, fixed to en-PH and Manila whatever locale or zone the caller passed. Same
 * defaults as the browser: a date call with no date field prints the numeric date, and so on.
 */
export function phFormat(d: Date, kind: 'date' | 'time' | 'datetime', opts?: Intl.DateTimeFormatOptions): string {
  if (!valid(d)) return ''
  const o: Intl.DateTimeFormatOptions = { ...(opts ?? {}) }
  delete o.timeZone
  const has = (fields: readonly string[]) => fields.some((f) => (o as Record<string, unknown>)[f] !== undefined)
  if ((kind === 'date' || kind === 'datetime') && !has(DATE_FIELDS) && !(kind === 'datetime' && has(TIME_FIELDS))) {
    Object.assign(o, { year: 'numeric', month: 'numeric', day: 'numeric' })
  }
  if ((kind === 'time' || kind === 'datetime') && !has(TIME_FIELDS) && !(kind === 'datetime' && has(DATE_FIELDS))) {
    Object.assign(o, { hour: 'numeric', minute: '2-digit', second: '2-digit' })
  }
  const key = JSON.stringify(o)
  let f = formatters.get(key)
  if (!f) { f = new Intl.DateTimeFormat(LOCALE, { ...o, timeZone: TIME_ZONE }); formatters.set(key, f) }
  return f.format(d)
}

/**
 * A calendar day ('YYYY-MM-DD') as a Date that every formatter here shows as that same day, on a
 * device in any zone: noon UTC is 8 PM in Manila. Use it to format a date that is not an instant
 * (a due date, a schedule column), instead of a local-midnight Date, which shows the day before
 * on a device east of Manila.
 */
export function phDay(day: string): Date {
  return new Date(`${day.slice(0, 10)}T12:00:00Z`)
}

export type ManilaParts = { year: number; month: number; day: number; hour: number; minute: number; second: number; weekday: number }

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

/** The Manila wall clock of an instant. `month` is 1 to 12; `weekday` is 0 for Sunday. */
export function manilaParts(d: DateLike): ManilaParts {
  const parts = F.parts.formatToParts(toDate(d))
  const at = (t: string) => parts.find((p) => p.type === t)?.value ?? '0'
  return {
    year: Number(at('year')),
    month: Number(at('month')),
    day: Number(at('day')),
    // Some engines print midnight as "24" with hour12:false.
    hour: Number(at('hour')) % 24,
    minute: Number(at('minute')),
    second: Number(at('second')),
    weekday: WEEKDAYS.indexOf(at('weekday')),
  }
}

const pad = (n: number) => String(n).padStart(2, '0')

/** The Manila calendar date of an instant, as 'YYYY-MM-DD'. */
export function manilaDayIso(d: DateLike = new Date()): string {
  const p = manilaParts(d)
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`
}

/** Manila is UTC+8 all year. Kept as a constant because nothing here may depend on the device. */
const OFFSET_MS = 8 * 3_600_000

/** A Manila wall clock ('YYYY-MM-DD', 'HH:mm[:ss[.mmm]]') as an ISO UTC instant, or null. */
export function manilaWallToIso(day: string, time = '00:00'): string | null {
  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day)
  const t = /^(\d{1,2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?$/.exec(time)
  if (!d || !t) return null
  const [y, mo, da] = [Number(d[1]), Number(d[2]), Number(d[3])]
  const [h, mi, s, fr] = [Number(t[1]), Number(t[2]), Number(t[3] ?? '0'), Number((t[4] ?? '0').padEnd(3, '0'))]
  if (mo < 1 || mo > 12 || da < 1 || da > 31 || h > 23 || mi > 59 || s > 59) return null
  const ms = Date.UTC(y, mo - 1, da, h, mi, s, fr) - OFFSET_MS
  return Number.isFinite(ms) ? new Date(ms).toISOString() : null
}

/** For <input type="datetime-local">: the Manila wall time of a stored instant, 'YYYY-MM-DDTHH:mm'. */
export function toInputDateTime(iso: DateLike | null | undefined): string {
  if (iso === null || iso === undefined || iso === '') return ''
  const x = toDate(iso)
  if (!valid(x)) return ''
  const p = manilaParts(x)
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`
}

/** For <input type="datetime-local">: read the value as Manila wall time. Null when empty or bad. */
export function fromInputDateTime(v: string | null | undefined): string | null {
  const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}(?::\d{2})?)/.exec(v ?? '')
  return m ? manilaWallToIso(m[1], m[2]) : null
}

/** For <input type="time">: the Manila 'HH:mm' of a stored instant. */
export function toInputTime(iso: DateLike | null | undefined): string {
  return fmtTime(iso, { h24: true })
}

// Money ----------------------------------------------------------------------------------------

const PESO = new Intl.NumberFormat(LOCALE, { style: 'currency', currency: CURRENCY })
const PESO_WHOLE = new Intl.NumberFormat(LOCALE, { style: 'currency', currency: CURRENCY, maximumFractionDigits: 0 })

/** 1250 -> '₱1,250.00'. `whole` drops the centavos, for summary figures. */
export function peso(amount: unknown, opts?: { whole?: boolean }): string {
  const n = Number(amount) || 0
  return (opts?.whole ? PESO_WHOLE : PESO).format(n)
}

/** 125000 centavos -> '₱1,250.00'. Escrow amounts are whole centavos (constitution I). */
export function pesoFromCentavos(c: number | null | undefined): string {
  return peso((Number(c) || 0) / 100)
}

// Mobile numbers -------------------------------------------------------------------------------

/**
 * A Philippine mobile number in any common form, as '+639XXXXXXXXX'. Null if it is not one.
 * Accepts 09XX XXX XXXX, 9XXXXXXXXX, 639XXXXXXXXX, +63 9XX-XXX-XXXX, (0917) 123 4567.
 */
export function normalisePhMobile(input: string | null | undefined): string | null {
  const digits = (input ?? '').replace(/[\s\-().]/g, '')
  let m: RegExpExecArray | null
  if ((m = /^\+?639(\d{9})$/.exec(digits))) return `+639${m[1]}`
  if ((m = /^09(\d{9})$/.exec(digits))) return `+639${m[1]}`
  if ((m = /^9(\d{9})$/.exec(digits))) return `+639${m[1]}`
  return null
}

/** '+639171234567' -> '0917 123 4567'. Anything that is not a Philippine mobile is returned as is. */
export function displayPhMobile(value: string | null | undefined): string {
  const e164 = normalisePhMobile(value)
  if (!e164) return value ?? ''
  const local = '0' + e164.slice(3)
  return `${local.slice(0, 4)} ${local.slice(4, 7)} ${local.slice(7)}`
}

/**
 * For a phone field that may hold a mobile or a landline: a Philippine mobile becomes
 * '+639XXXXXXXXX'; anything else is kept as typed, trimmed. Empty becomes null.
 */
export function tidyPhone(value: string | null | undefined): string | null {
  const v = (value ?? '').trim()
  if (!v) return null
  return normalisePhMobile(v) ?? v
}
